import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from "react";
import { useDispatch } from "react-redux";
import {
	resendWelcomeEmail,
	setUserEnabledDisabled,
	createDatabaseUserRecord,
	deleteUser,
	setUserMFADisabled,
	unlockAllDocuments,
	adminSetEmailVerified,
	fetchTenantDetail,
	sendBulkWelcomeEmails,
} from "../../actions/adminActions";

import CustomizableTable from "../Tables/CustomizableTable";
import InteractiveTableCell from "../Tables/InteractiveTableCell";
import {
	withPills,
	buildCellWithPills,
	getCellText,
} from "../Tables/CellWithPills";
import { ButtonWithRouter } from "../ButtonWithIcon";
import { DropDownButtonWithRouter } from "../DropDownButton";

import { openGlobalDialog } from "../../actions/globalEditDialogActions";
import { IconUser } from "@tabler/icons-react";
import { LexWorkplaceGavelIcon } from "../LexWorkplaceIcons";
import { isTenantSuspended } from "./firmStatusPill";
import { SuspendedFirmUsersMessage } from "../Utils/UnderSubHeaderWarningMessages";

const extractCognitoAttribute = (attributes, attributeName) => {
	const att = attributes.filter((a) => a.Name === attributeName);
	return att.length ? att[0].Value : "";
};

const MFA_STATUS_DISPLAY = {
	MFA_ENABLED: "Enabled",
	MFA_DISABLED: "Not Set",
	MFA_UNKNOWN: "Unknown",
};

const getMfaStatusDisplay = (mfaStatus) =>
	MFA_STATUS_DISPLAY[mfaStatus] || "Unknown";

const buildRolesPills = (roles, config) => ({
	text: "",
	pills: roles.map((role) => ({ label: role, ...(config[role] || {}) })),
});

const pillConfigs = {
	roles: {
		"Uptime Admin": { style: { backgroundColor: "var(--lxw-main-600)" } },
		"Firm Admin": { style: { backgroundColor: "var(--lw-dark-grey)" } },
		"LexWorkplace Admin": {
			style: {
				color: "var(--lw-dark-grey)",
				backgroundColor: "var(--lw-gold)",
			},
			title: "User has access to LexWorkplace Admin page.  For Uptime colleagues only.",
		},
	},
	emailAddress: {
		EmailMismatch: {
			style: { color: "white", backgroundColor: "var(--lw-darker-red)" },
			title: `Email address in Cognito does not match address in database.  Run the "Create/Update LexWorkplace Database User Record" for this user.`,
			displayText: "Mismatch",
		},
		Unverified: {
			style: {
				color: "var(--lw-dark-grey)",
				backgroundColor: "var(--lw-gold)",
			},
			title: "User has not verified their email address.  They will not be able to log in via email address",
		},
	},
	username: {
		UsernameMismatch: {
			style: { color: "white", backgroundColor: "var(--lw-darker-red)" },
			title: `Username in Cognito does not match username in database.  Run the "Create/Update LexWorkplace Database User Record" for this user.  Also this shouldn't be possible...`,
			displayText: "Mismatch",
		},
	},
	displayName: {
		DisplayNameMismatch: {
			style: { color: "white", backgroundColor: "var(--lw-darker-red)" },
			title: `User display name in Cognito does not match display name in database.  Run the "Create/Update LexWorkplace Database User Record" for this user.`,
			displayText: "Mismatch",
		},
	},
	status: {
		"No Cognito User": {
			style: { color: "white", backgroundColor: "var(--lw-darker-red)" },
			title: `There is no Cognito user with this sub.`,
		},
		"No DB User": {
			style: { color: "white", backgroundColor: "var(--lw-darker-red)" },
			title: `There is no user in the database with this sub.`,
		},
		"Has Not Logged In": {
			style: {
				color: "var(--lw-dark-grey)",
				backgroundColor: "var(--lw-gold)",
			},
			title: `This user has not logged in for the first time yet`,
		},
		"Welcome Email Deferred": {
			style: {
				color: "var(--lw-dark-grey)",
				backgroundColor: "var(--lw-gold)",
			},
			title: `Welcome email was deferred during bulk import. Use "Send All Deferred Welcome Emails" to send it.`,
		},
	},
};

function processTenantUsers(res) {
	var cognitoUsers = {};
	var dbUsers = {};
	var lexworkplaceAdminUsers = {};
	var lockedDocuments = res.mysql.lockedDocuments;
	res.mysql.cognito.Users.forEach((u) => {
		const emailVerifiedAttrValue = extractCognitoAttribute(
			u.Attributes,
			"email_verified"
		);

		const emailVerified = emailVerifiedAttrValue === "false" ? false : true;

		const cognitoUserEnabled = u.Enabled;

		cognitoUsers[extractCognitoAttribute(u.Attributes, "sub")] = {
			UserStatus: u.UserStatus,
			UserCreateDate: u.UserCreateDate,
			emailAddress: extractCognitoAttribute(u.Attributes, "email"),
			name: extractCognitoAttribute(u.Attributes, "name"),
			username: u.Username,
			emailVerified,
			cognitoUserEnabled,
			mfaStatus: u.mfaStatus,
		};
	});
	res.mysql.lexworkplaceAdmins.Users.forEach((u) => {
		lexworkplaceAdminUsers[extractCognitoAttribute(u.Attributes, "sub")] =
			1;
	});
	var usersToShow = res.mysql.users.map((u) => {
		var roles = [];
		if (lexworkplaceAdminUsers[u.sub]) {
			roles.push("LexWorkplace Admin");
		}
		if (u.type === "uptimeAdmin") {
			roles.push("Uptime Admin");
		}
		if (
			res.mysql.firmAdmins.filter(
				(firmAdmin) => firmAdmin.userSub === u.sub
			).length > 0
		) {
			roles.push("Firm Admin");
		}

		var userRow = { ...u, roles };
		dbUsers[u.sub] = { ...u, roles };
		if (cognitoUsers[u.sub]) {
			userRow.cognitoStatus = cognitoUsers[u.sub].UserStatus;
			userRow.cognitoCreatedDate = cognitoUsers[u.sub].UserCreateDate;
			userRow.cognitoEmailAddress = cognitoUsers[u.sub].emailAddress;
			userRow.cognitoUsername = cognitoUsers[u.sub].username;
			userRow.emailVerified = cognitoUsers[u.sub].emailVerified;
			userRow.cognitoUserEnabled = cognitoUsers[u.sub].cognitoUserEnabled;
			userRow.cognitoDisplayName = cognitoUsers[u.sub].name;
			userRow.mfaStatus = getMfaStatusDisplay(
				cognitoUsers[u.sub].mfaStatus
			);
		} else {
			userRow.cognitoUserMissing = true;
		}

		userRow.lockedDocuments = lockedDocuments[u.sub]
			? lockedDocuments[u.sub].numLocked
			: 0;
		return userRow;
	});
	res.mysql.cognito.Users.forEach((u) => {
		const sub = extractCognitoAttribute(u.Attributes, "sub");
		if (!dbUsers[sub]) {
			usersToShow.push({
				cognitoUsername: u.Username,
				sub,
				cognitoEmailAddress: extractCognitoAttribute(
					u.Attributes,
					"email"
				),
				cognitoDisplayName: extractCognitoAttribute(
					u.Attributes,
					"name"
				),
				cognitoUserEnabled: u.Enabled,
				cognitoStatus: u.UserStatus,
				cognitoCreatedDate: u.UserCreateDate,
				dbUserMissing: true,
				roles: lexworkplaceAdminUsers[sub]
					? ["LexWorkplace Admin"]
					: [],
				mfaStatus: getMfaStatusDisplay(u.mfaStatus),
			});
		}
	});

	usersToShow = usersToShow.map((user) => {
		const missingOneOrTheOther =
			user.cognitoUserMissing || user.dbUserMissing;

		const username = buildCellWithPills(
			user.cognitoUsername,
			[
				[
					() =>
						!missingOneOrTheOther &&
						user.username !== user.cognitoUsername,
					"UsernameMismatch",
				],
			],
			pillConfigs.username
		);

		const displayName = buildCellWithPills(
			user.displayName,
			[
				[
					() =>
						!missingOneOrTheOther &&
						user.displayName !== user.cognitoDisplayName,
					"DisplayNameMismatch",
				],
			],
			pillConfigs.displayName
		);

		const emailAddress = buildCellWithPills(
			user.cognitoEmailAddress,
			[
				[
					() =>
						!missingOneOrTheOther &&
						user.emailAddress !== user.cognitoEmailAddress,
					"EmailMismatch",
				],
				[() => user.emailVerified === false, "Unverified"],
			],
			pillConfigs.emailAddress
		);

		const status = buildCellWithPills(
			user.cognitoUserMissing
				? ""
				: user.cognitoUserEnabled
					? "Active"
					: "Disabled",
			[
				[() => user.cognitoUserMissing, "No Cognito User"],
				[() => user.dbUserMissing, "No DB User"],
				[() => !!user.welcomeEmailSuppressed, "Welcome Email Deferred"],
				[
					() => user.cognitoStatus === "FORCE_CHANGE_PASSWORD",
					"Has Not Logged In",
				],
			],
			pillConfigs.status
		);

		const roles = buildRolesPills(user.roles, pillConfigs.roles);

		return {
			...user,
			username,
			displayName,
			emailAddress,
			status,
			roles,
		};
	});

	return {
		users: usersToShow,
		title: `"${res.mysql.tenant.name}", tenantID:${res.mysql.tenant.id}`,
		numDeferredWelcomeEmails: usersToShow.filter(
			(u) => u.welcomeEmailSuppressed
		).length,
	};
}

function AdminUsersForTenant({ tenantID, tenantDetail }) {
	const dispatch = useDispatch();
	const isSuspended = isTenantSuspended(
		tenantDetail?.mysql?.tenant?.planType
	);
	const [users, setUsers] = useState([]);
	const [numDeferredWelcomeEmails, setNumDeferredWelcomeEmails] = useState(0);
	const [title, setTitle] = useState("");
	const [loading, setLoading] = useState(true);
	const isMounted = useRef(true);
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const loadUsers = useCallback(() => {
		setLoading(true);
		dispatch(fetchTenantDetail(tenantID))
			.then((res) => {
				const processed = processTenantUsers(res);
				setUsers(processed.users);
				setTitle(processed.title);
				setNumDeferredWelcomeEmails(processed.numDeferredWelcomeEmails);
				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setLoading(false);
			});
	}, [tenantID]);

	useEffect(() => {
		if (!tenantDetail) return;
		const processed = processTenantUsers(tenantDetail);
		setUsers(processed.users);
		setTitle(processed.title);
		setNumDeferredWelcomeEmails(processed.numDeferredWelcomeEmails);
		setLoading(false);
	}, [tenantDetail]);

	const deleteUserIntent = useCallback(
		(username, sub, tenantIDParam) => {
			dispatch(
				openGlobalDialog(
					{
						label: "Delete this user?",
						mainText: `This will completely delete both the Cognito and database user record for:"${username}".  If this is a testing/QA/internal user account, they will no longer be able to log in to any other lexworkplace environments (dev/newfeature/staging).  Please proceed with caution.  To delete the user, type "delete" in the text field.`,
						value: "",
						submitButtonLabel: "Delete",
					},
					() => {
						dispatch(
							deleteUser(username, sub, tenantIDParam, loadUsers)
						);
					},
					(state) => {
						return {
							canSubmit: state.value === "delete",
							erroMessage: "",
						};
					}
				)
			);
		},
		[dispatch, loadUsers]
	);

	const { usersColumnConfig } = useMemo(() => {
		const suspendedOverride = isSuspended
			? { disabled: true, disabledHoverText: "This firm is suspended" }
			: {};
		const usernamePill = withPills(
			{ Header: "User Name", accessor: "username" },
			{
				renderCell: (pillsEl, row) => (
					<InteractiveTableCell
						menuItems={[
							{
								text: "Edit User",
								disabled:
									row.original.dbUserMissing ||
									row.original.cognitoUserMissing,
								href: `/admin/tenant/${tenantID}/user/edit/${row.original.sub}`,
							},
							{
								text: row.original.welcomeEmailSuppressed
									? "Send Welcome Email"
									: "Re-send Welcome Email",
								disabled:
									row.original.cognitoStatus !==
									"FORCE_CHANGE_PASSWORD",
								onSelect: () =>
									dispatch(
										resendWelcomeEmail(
											row.original.cognitoUsername,
											tenantID
										)
									),
								...suspendedOverride,
							},
							{
								text: row.original.cognitoUserEnabled
									? "Disable User"
									: "Enable User",
								disabled:
									row.original.dbUserMissing ||
									row.original.cognitoUserMissing,
								onSelect: () =>
									dispatch(
										setUserEnabledDisabled({
											newEnabled:
												!row.original
													.cognitoUserEnabled,
											username:
												row.original.cognitoUsername,
											sub: row.original.sub,
											tenantID: tenantID,
											successCallback: loadUsers,
										})
									),
								...suspendedOverride,
							},
							{
								text: "Create/Update LexWorkplace Database User Record",
								disabled: row.original.cognitoUserMissing,
								onSelect: () =>
									dispatch(
										createDatabaseUserRecord(
											tenantID,
											row.original,
											loadUsers
										)
									),
								...suspendedOverride,
							},
							{
								text: "Delete User",
								disabled: !(
									row.original.cognitoUserMissing ||
									row.original.cognitoStatus ===
										"FORCE_CHANGE_PASSWORD"
								),
								onSelect: () =>
									deleteUserIntent(
										row.original.cognitoUsername,
										row.original.sub,
										tenantID
									),
								...suspendedOverride,
							},
							{
								text: "Disable MFA",
								disabled:
									row.original.dbUserMissing ||
									row.original.cognitoUserMissing,
								onSelect: () =>
									dispatch(
										setUserMFADisabled(
											row.original.cognitoUsername,
											loadUsers
										)
									),
								...suspendedOverride,
							},
							{
								text: "Set Email as Verified",
								disabled: row.original.emailVerified,
								onSelect: () =>
									dispatch(
										adminSetEmailVerified(
											row.original.cognitoUsername,
											loadUsers
										)
									),
								...suspendedOverride,
							},
							{
								text: row.original.lockedDocuments
									? `Unlock ${row.original.lockedDocuments} Documents`
									: "No Locked Documents",
								disabled: !row.original.lockedDocuments,
								onSelect: () =>
									dispatch(
										unlockAllDocuments({
											sub: row.original.sub,
											tenantID: tenantID,
											successCallback: loadUsers,
										})
									),
								...suspendedOverride,
							},
						]}
						copyButtonValue={getCellText(row.value)}
					>
						{pillsEl}
					</InteractiveTableCell>
				),
			}
		);

		const displayNamePill = withPills(
			{ Header: "Display Name", accessor: "displayName" },
			{
				renderCell: (pillsEl, row) => (
					<InteractiveTableCell
						copyButtonValue={getCellText(row.value)}
					>
						{pillsEl}
					</InteractiveTableCell>
				),
			}
		);

		const emailPill = withPills(
			{ Header: "Email", accessor: "emailAddress" },
			{
				renderCell: (pillsEl, row) => (
					<InteractiveTableCell
						copyButtonValue={getCellText(row.value)}
					>
						{pillsEl}
					</InteractiveTableCell>
				),
			}
		);

		const statusPill = withPills({
			Header: "Status",
			accessor: "status",
		});

		const rolesPill = withPills({
			Header: "Roles",
			accessor: "roles",
		});

		const columns = [
			usernamePill,
			displayNamePill,
			emailPill,
			statusPill,
			rolesPill,
			{
				Header: "MFA Status",
				accessor: "mfaStatus",
			},
			{
				Header: "Sub",
				accessor: "sub",
				Cell: (row) => (
					<InteractiveTableCell copyButtonValue={row.value}>
						{row.value}
					</InteractiveTableCell>
				),
			},
			{ Header: "Created Date", accessor: "cognitoCreatedDate" },
		];

		return { usersColumnConfig: columns };
	}, [tenantID, dispatch, deleteUserIntent, loadUsers, isSuspended]);

	return (
		<div className="fullHeightFlex">
			{isSuspended ? SuspendedFirmUsersMessage() : null}
			<CustomizableTable
				actionButtons={({ openCsvExport }) => (
					<div
						style={{
							marginLeft: "auto",
							marginTop: 0,
						}}
						className="buttonGroup"
					>
						<ButtonWithRouter
							redirect={`/admin/tenant/${tenantID}/user/new`}
							buttonText="New User"
							iconComponent={<IconUser size={16} />}
							disabled={isSuspended}
							disabledHoverText="This firm is suspended"
						/>
						<DropDownButtonWithRouter
							title="Actions"
							iconComponent={<LexWorkplaceGavelIcon />}
							pullRight
							menuItems={[
								{
									text: "Edit Firm",
									href: `/admin/tenant/edit/${tenantID}`,
								},
								{
									text: "Export Data as CSV",
									onSelect: openCsvExport,
								},
								{
									text: `Send All (${numDeferredWelcomeEmails}) Deferred Welcome Emails`,
									disabled:
										numDeferredWelcomeEmails === 0 ||
										isSuspended,
									disabledHoverText: isSuspended
										? "This firm is suspended"
										: undefined,
									onSelect: () =>
										dispatch(
											sendBulkWelcomeEmails(
												tenantID,
												() => {
													if (isMounted.current)
														loadUsers();
												}
											)
										),
								},
							]}
						/>
					</div>
				)}
				leftSideFilterSpace={
					<h4 style={{ paddingLeft: 12, alignContent: "center" }}>
						<IconUser
							size={20}
							style={{ marginRight: 10, verticalAlign: "bottom" }}
						/>
						{title}
					</h4>
				}
				className="fullWidth"
				loading={loading}
				data={users}
				columns={usersColumnConfig}
				csvExport={{
					fileName: "admin-users",
				}}
				noResultsText={"No Users"}
				autoResize
				tableUniqueID="/admin/usersForFirm"
			/>
		</div>
	);
}

export default AdminUsersForTenant;
